'use client'

import { useEffect, useState } from 'react'
import { cn } from '@/lib/utils'

type InstallStatus = {
  has_ever_received: boolean
  last_event_at: string | null
  last_event_type: string | null
  last_event_name: string | null
  last_hour_count: number
  last_day_count: number
}

type Props = {
  /** Legacy admin path (brand-scoped). Used when statusUrl is not provided. */
  brandId?: string
  /**
   * Customer-facing path override — e.g. `/api/tags/${containerId}/install-status`.
   * When set, this URL is used and brandId is ignored for fetching.
   */
  statusUrl?: string
  installSnippet: string
  /**
   * Which sections to render. `'all'` (default) shows the full panel; the
   * other values are used by the header to split the panel into two distinct
   * popovers (Install + Block your tracking).
   */
  section?: 'all' | 'install' | 'opt_out'
  /** Hide the snippet block (e.g. when the host renders its own code panel). */
  hideSnippet?: boolean
}

/**
 * Install + status panel for a tag container.
 *
 * NOTE: lives under `components/admin/` for historical reasons — also used by
 * the customer-facing `/tags` surface via `statusUrl`. Safe to keep here; if
 * we ever build a "customer-only" components directory, move it there.
 *
 * Surfaces:
 *   - The install snippet with copy
 *   - Step-by-step install instructions
 *   - Live install status (events received in the last hour / day, last seen)
 *   - Opt-out bookmarklet for team members
 *
 * Used by both /admin/tag-manager (brand-scoped, via brandId) and /tags
 * (workspace-scoped, via statusUrl).
 */
export function TagInstallPanel({ brandId, statusUrl, installSnippet, section = 'all', hideSnippet = false }: Props) {
  const showInstall = section === 'all' || section === 'install'
  const showOptOut = section === 'all' || section === 'opt_out'
  const [status, setStatus] = useState<InstallStatus | null>(null)
  const [statusLoading, setStatusLoading] = useState(false)
  const [statusError, setStatusError] = useState<string | null>(null)
  const [copied, setCopied] = useState<'snippet' | 'bookmarklet' | null>(null)
  const [optOutHere, setOptOutHere] = useState<boolean | null>(null)

  // ── Live install status ──
  const effectiveStatusUrl = statusUrl
    ?? (brandId ? `/api/admin/tag-manager/${brandId}/install-status` : null)

  const refreshStatus = async () => {
    if (!effectiveStatusUrl) {
      setStatus(null)
      return
    }
    setStatusLoading(true)
    setStatusError(null)
    try {
      const res = await fetch(effectiveStatusUrl)
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      setStatus(await res.json())
    } catch (e) {
      setStatusError(e instanceof Error ? e.message : 'Failed to fetch status')
    } finally {
      setStatusLoading(false)
    }
  }

  useEffect(() => {
    void refreshStatus()
    if (!effectiveStatusUrl) return
    // Auto-refresh every 30s while panel is open
    const id = setInterval(refreshStatus, 30_000)
    return () => clearInterval(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveStatusUrl])

  // ── Opt-out state on this domain ──
  useEffect(() => {
    try {
      setOptOutHere(window.localStorage.getItem('__atrium_optout') === '1')
    } catch {
      setOptOutHere(null)
    }
  }, [])

  // ── Copy helpers ──
  const copy = (text: string, kind: 'snippet' | 'bookmarklet') => {
    navigator.clipboard.writeText(text)
    setCopied(kind)
    setTimeout(() => setCopied(prev => (prev === kind ? null : prev)), 2000)
  }

  // ── Bookmarklet — toggles localStorage flag with a clear UX ──
  // Compressed deliberately so the URL is the bookmarklet payload.
  const bookmarklet =
    `javascript:(function(){var k='__atrium_optout';if(localStorage.getItem(k)){localStorage.removeItem(k);alert('Atrium tracking: ENABLED on '+location.host);}else{localStorage.setItem(k,'1');alert('Atrium tracking: BLOCKED on '+location.host);}})();`

  const setOptOutLocally = (value: boolean) => {
    try {
      if (value) window.localStorage.setItem('__atrium_optout', '1')
      else window.localStorage.removeItem('__atrium_optout')
      setOptOutHere(value)
    } catch { /* ignore */ }
  }

  return (
    <div className="space-y-4 max-w-md">

      {showInstall && (
      <>
      {!hideSnippet && (
      <>
      {/* ── Install snippet ── */}
      <section>
        <div className="text-xs font-semibold text-atrium-black mb-2">Install snippet</div>
        <p className="text-xs text-atrium-muted mb-3">
          Paste in the {'<head>'} of every page on your site. One install covers
          all tags + triggers configured here.
        </p>
        <div className="bg-neutral-950 rounded-lg p-3 font-mono text-xs text-green-400 break-all mb-2">
          {installSnippet}
        </div>
        <button
          onClick={() => copy(installSnippet, 'snippet')}
          className={cn(
            'flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg transition-colors',
            copied === 'snippet'
              ? 'bg-green-50 text-green-700'
              : 'bg-atrium-content hover:bg-neutral-200 text-atrium-black'
          )}
        >
          {copied === 'snippet' ? '✓ Copied' : 'Copy snippet'}
        </button>
      </section>
      </>
      )}

      {/* ── Live status ── */}
      <section>
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs font-semibold text-atrium-black">Install status</div>
          <button
            onClick={refreshStatus}
            disabled={statusLoading}
            className="text-[11px] text-atrium-muted hover:text-atrium-black disabled:opacity-50"
          >
            {statusLoading ? 'Checking…' : 'Refresh'}
          </button>
        </div>

        {statusError ? (
          <div className="text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2">{statusError}</div>
        ) : !status ? (
          <div className="text-xs text-atrium-muted">Checking…</div>
        ) : !status.has_ever_received ? (
          <div className="text-xs bg-amber-50 text-amber-800 rounded-lg px-3 py-2">
            <div className="font-medium mb-0.5">No events received yet</div>
            <div className="text-[11px] text-amber-700/90">
              Once the snippet is installed and visitors load the site, events
              will land here within ~1 minute. If you&apos;ve already installed it,
              try opening the site in an incognito window.
            </div>
          </div>
        ) : status.last_hour_count > 0 ? (
          <div className="text-xs bg-green-50 text-green-800 rounded-lg px-3 py-2">
            <div className="flex items-center gap-2 font-medium mb-0.5">
              <span className="size-1.5 rounded-full bg-green-500 animate-pulse" />
              Live — {status.last_hour_count.toLocaleString()} event{status.last_hour_count === 1 ? '' : 's'} in the last hour
            </div>
            <div className="text-[11px] text-green-700/90">
              {status.last_day_count.toLocaleString()} in last 24h · last seen {timeAgo(status.last_event_at)}
              {status.last_event_type ? ` (${status.last_event_type}${status.last_event_name ? `: ${status.last_event_name}` : ''})` : null}
            </div>
          </div>
        ) : (
          <div className="text-xs bg-atrium-content text-atrium-black rounded-lg px-3 py-2">
            <div className="font-medium mb-0.5">Quiet right now</div>
            <div className="text-[11px] text-atrium-muted">
              {status.last_day_count.toLocaleString()} in last 24h · last seen {timeAgo(status.last_event_at)}.
              No events in the last hour. Could be normal for low-traffic sites.
            </div>
          </div>
        )}
      </section>

      {/* ── Install instructions ── */}
      <section>
        <details className="group">
          <summary className="text-xs font-semibold text-atrium-black cursor-pointer hover:text-accent-600 list-none flex items-center gap-1">
            <span className="text-atrium-muted group-open:rotate-90 transition-transform">▸</span>
            Install instructions
          </summary>
          <ol className="mt-3 ml-4 space-y-1.5 text-xs text-atrium-muted list-decimal">
            <li>Open your site&apos;s HTML template (or your tag manager).</li>
            <li>Paste the snippet inside the <code className="px-1 py-0.5 rounded bg-atrium-content text-atrium-black">{'<head>'}</code> tag, ideally near the top.</li>
            <li>Deploy. The script loads <em>async</em> and won&apos;t block your page.</li>
            <li>
              Open your site in a new browser (or incognito), then come back
              and click <strong>Refresh</strong> on the status above. Events
              should appear within ~1 minute.
            </li>
            <li>
              If nothing arrives: confirm the snippet is in the rendered HTML
              (<code className="px-1 py-0.5 rounded bg-atrium-content text-atrium-black">view-source:</code>),
              the URL <code className="px-1 py-0.5 rounded bg-atrium-content text-atrium-black">t.atrium.run/a.js</code> resolves,
              and your CSP allows <code className="px-1 py-0.5 rounded bg-atrium-content text-atrium-black">cdn.ravm.tv</code> if Universal Ads is enabled.
            </li>
          </ol>
        </details>
      </section>

      </>
      )}

      {showOptOut && (
      <>
      {/* ── Opt-out ── */}
      <section>
        <details className="group" open={section === 'opt_out'}>
          <summary className="text-xs font-semibold text-atrium-black cursor-pointer hover:text-accent-600 list-none flex items-center gap-1">
            <span className="text-atrium-muted group-open:rotate-90 transition-transform">▸</span>
            Block your own tracking
          </summary>
          <div className="mt-3 space-y-3">
            <p className="text-xs text-atrium-muted">
              Atrium team members shouldn&apos;t pollute their own audience data.
              The opt-out flag is stored in <code className="px-1 py-0.5 rounded bg-atrium-content text-atrium-black">localStorage</code> per origin
              and honored by both the marketing pixel and any tag installed via this manager.
            </p>

            {/* Local toggle for whatever origin you're currently on */}
            {optOutHere !== null && (
              <div className="flex items-center justify-between bg-atrium-content rounded-lg px-3 py-2">
                <div className="text-xs">
                  <div className="font-medium text-atrium-black">
                    On this domain ({typeof window !== 'undefined' ? window.location.host : ''}):
                  </div>
                  <div className={cn('text-[11px]', optOutHere ? 'text-green-700' : 'text-atrium-muted')}>
                    {optOutHere ? 'Opted out — no events from your browser' : 'Tracking enabled'}
                  </div>
                </div>
                <button
                  onClick={() => setOptOutLocally(!optOutHere)}
                  className={cn(
                    'text-xs font-medium px-3 py-1.5 rounded-lg transition-colors',
                    optOutHere
                      ? 'bg-atrium-inner border border-atrium-border hover:bg-neutral-200 text-atrium-black'
                      : 'bg-atrium-black text-white hover:bg-neutral-800'
                  )}
                >
                  {optOutHere ? 'Re-enable' : 'Opt out'}
                </button>
              </div>
            )}

            <div>
              <div className="text-xs font-medium text-atrium-black mb-1.5">Bookmarklet (works on any site)</div>
              <p className="text-[11px] text-atrium-muted mb-2">
                Drag this to your bookmarks bar, then click it on any site
                running our tag to toggle blocking. localStorage is per-origin,
                so each site needs its own toggle.
              </p>
              <a
                href={bookmarklet}
                onClick={(e) => e.preventDefault()}
                className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg bg-accent-50 text-accent-700 hover:bg-accent-100 cursor-grab"
                title="Drag to bookmarks bar"
              >
                ⌬ Atrium opt-out
              </a>
              <button
                onClick={() => copy(bookmarklet, 'bookmarklet')}
                className={cn(
                  'ml-2 text-xs font-medium px-3 py-1.5 rounded-lg transition-colors',
                  copied === 'bookmarklet'
                    ? 'bg-green-50 text-green-700'
                    : 'bg-atrium-content hover:bg-neutral-200 text-atrium-black'
                )}
              >
                {copied === 'bookmarklet' ? '✓ Copied' : 'Copy bookmarklet'}
              </button>
            </div>

            <div>
              <div className="text-xs font-medium text-atrium-black mb-1.5">Console one-liner</div>
              <pre className="bg-neutral-950 rounded-lg p-2.5 text-[11px] text-green-400 overflow-x-auto">{`window.atrium && atrium.optOut()  // or:\nlocalStorage.setItem('__atrium_optout', '1')`}</pre>
            </div>
          </div>
        </details>
      </section>
      </>
      )}

    </div>
  )
}

function timeAgo(iso: string | null): string {
  if (!iso) return 'never'
  const ms = Date.now() - new Date(iso).getTime()
  if (ms < 60_000) return 'just now'
  if (ms < 60 * 60_000) return `${Math.floor(ms / 60_000)}m ago`
  if (ms < 24 * 60 * 60_000) return `${Math.floor(ms / 3_600_000)}h ago`
  return `${Math.floor(ms / 86_400_000)}d ago`
}
